/**
 * Enhanced AdminLibraryPage using the new component system
 * Demonstrates content library management with enhanced table features
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiBookOpen, FiEdit, FiEye, FiPlus, FiSearch } from 'react-icons/fi';

// New component system imports
import {
  AdminLayout,
  Breadcrumb,
  DataTable,
  TableColumn,
} from '@grcs/components';
import {
  VStack,
  HStack,
  Text,
  Badge,
  Heading,
  Flex,
  Stack,
  Select,
  Box,
  useColorModeValue,
  Input,
  InputGroup,
  InputLeftElement,
  Switch,
  FormControl,
  FormLabel,
  useToast,
  Button,
} from '@chakra-ui/react';

// Existing hooks and types
import { useData } from '@grcs/context/DataContext';
import { useTableSelectedData } from '@grcs/hook/useTableData';
import { ArticleProfile } from '@grcs/types/DataTypes';
import { countArticleCharacters } from '@grcs/utils/content/countArticleCharacters';
import { AdminSummary } from '../shared';
import { TABLE_NAMES } from '@grcs/utils/database/types';

interface LibraryStats {
  totalArticles: number;
  byGrade: Record<string, number>;
  byChapter: Record<string, number>;
  recentArticles: number;
}

export const AdminLibraryPageEnhanced: React.FC = () => {
  const navigate = useNavigate();
  const surface = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.600');
  const muted = useColorModeValue('gray.600', 'gray.300');
  const toast = useToast();
  const { supabase } = useData();
  const {
    data: libraryTableData,
    error: libraryError,
    isLoading: isLibraryLoading,
  } = useTableSelectedData(
    TABLE_NAMES.LIBRARY,
    'id, title, author, grade, chapter, created_at, deleted, word_count',
  );

  const [contents, setContents] = useState<ArticleProfile[]>([]);
  const [filteredContents, setFilteredContents] = useState<ArticleProfile[]>(
    [],
  );
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedGrade, setSelectedGrade] = useState<string>('');
  const [selectedChapter, setSelectedChapter] = useState<string>('');
  const [stats, setStats] = useState<LibraryStats>({
    totalArticles: 0,
    byGrade: {},
    byChapter: {},
    recentArticles: 0,
  });
  const isLoading = isLibraryLoading;
  const [adminToolsEnabled, setAdminToolsEnabled] = useState(false);
  const [isUpdatingWordCount, setIsUpdatingWordCount] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Load and process library data
  useEffect(() => {
    if (libraryTableData) {
      const activeContents = libraryTableData.filter(
        (content: ArticleProfile) => !content.deleted,
      );
      setContents(activeContents);
      setFilteredContents(activeContents);

      // Calculate stats
      const gradeCount: Record<string, number> = {};
      const chapterCount: Record<string, number> = {};
      let recentCount = 0;
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

      activeContents.forEach((content: ArticleProfile) => {
        // Count by grade
        gradeCount[content.grade] = (gradeCount[content.grade] || 0) + 1;

        // Count by chapter
        chapterCount[content.chapter] =
          (chapterCount[content.chapter] || 0) + 1;

        // Count recent articles
        if (new Date(content.created_at) > weekAgo) {
          recentCount++;
        }
      });

      setStats({
        totalArticles: activeContents.length,
        byGrade: gradeCount,
        byChapter: chapterCount,
        recentArticles: recentCount,
      });
    }
  }, [libraryTableData]);

  // Apply filters and search
  useEffect(() => {
    let filtered = contents;

    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase().trim();

      filtered = filtered.filter((content) => {
        const title = (content.title || '').toLowerCase();
        const grade = (content.grade || '').toString().trim();
        const chapter = (content.chapter || '').toString().trim();

        // Check if query matches title
        if (title.includes(query)) return true;

        // Check if query matches grade
        // Supports: "4", "grade4", "grade 4"
        if (
          query === grade ||
          query === `grade${grade}` ||
          query === `grade ${grade}`
        )
          return true;

        // Check if query matches chapter
        // Supports: "5", "chapter5", "chapter 5"
        if (
          query === chapter ||
          query === `chapter${chapter}` ||
          query === `chapter ${chapter}`
        )
          return true;

        return false;
      });
    }

    if (selectedGrade) {
      filtered = filtered.filter((content) => content.grade === selectedGrade);
      filtered.sort((a, b) => {
        const toNum = (v: string) => {
          return Number(String(v ?? '').trim());
        };
        return toNum(a.chapter?.toString()) - toNum(b.chapter?.toString());
      });
    }

    if (selectedChapter) {
      filtered = filtered.filter(
        (content) => content.chapter === selectedChapter,
      );
    }

    setFilteredContents(filtered);
  }, [contents, searchQuery, selectedGrade, selectedChapter]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedGrade, selectedChapter]);

  // Event handlers
  const handleRowClick = (content: ArticleProfile) => {
    // Always navigate to view/preview when clicking row
    navigate(`/admin/library/${content.id}/view`);
  };

  const handleAddNew = () => {
    navigate('/admin/library/new');
  };

  const handleEdit = (content: ArticleProfile) => {
    navigate(`/admin/library/${content.id}/edit`);
  };

  const handleView = (content: ArticleProfile) => {
    navigate(`/admin/library/${content.id}/view`);
  };

  const handleUpdateWordCount = async () => {
    setIsUpdatingWordCount(true);

    try {
      const { data, error } = await supabase
        .from(TABLE_NAMES.LIBRARY)
        .select('id, title, article, word_count')
        .eq('deleted', false)
        .eq('word_count', 0);

      if (error) {
        throw error;
      }

      const articles = (data as ArticleProfile[]) || [];
      const updatedArticles: Array<{
        id: string;
        title: string;
        previous_word_count: number;
        word_count: number;
      }> = [];

      for (const article of articles) {
        const wordCount = countArticleCharacters(article.article || '');
        const { error: updateError } = await supabase
          .from(TABLE_NAMES.LIBRARY)
          .update({ word_count: wordCount })
          .eq('id', article.id);

        if (updateError) {
          throw updateError;
        }

        updatedArticles.push({
          id: article.id,
          title: article.title,
          previous_word_count: article.word_count || 0,
          word_count: wordCount,
        });
      }

      console.log('[AdminLibrary] Updated article word counts', {
        count: updatedArticles.length,
        articles: updatedArticles,
      });

      setContents((prev) =>
        prev.map((content) => {
          const updated = updatedArticles.find(
            (article) => article.id === content.id,
          );
          return updated
            ? { ...content, word_count: updated.word_count }
            : content;
        }),
      );

      const updatedTitles = updatedArticles.map((article) => article.title);
      const visibleTitles = updatedTitles.slice(0, 5);
      const remainingCount = updatedTitles.length - visibleTitles.length;
      const description =
        updatedTitles.length === 0
          ? 'No articles with 0 word count were found.'
          : [
              `Updated ${updatedTitles.length} article(s):`,
              visibleTitles.join(', '),
              remainingCount > 0 ? `and ${remainingCount} more.` : '',
            ]
              .filter(Boolean)
              .join(' ');

      toast({
        title: 'Word counts updated',
        description,
        status: 'success',
        duration: 8000,
        isClosable: true,
      });
    } catch (error) {
      console.error('[AdminLibrary] Failed to update word counts', error);
      toast({
        title: 'Failed to update word counts',
        description:
          error instanceof Error ? error.message : 'Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUpdatingWordCount(false);
    }
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  // Calculate pagination info
  const totalItems = filteredContents.length;
  const totalPages = Math.ceil(totalItems / pageSize);

  // Get unique values for filters
  const availableGrades = [...new Set(contents.map((c) => c.grade))].sort(
    (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }),
  );
  const availableChapters = [...new Set(contents.map((c) => c.chapter))].sort(
    (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }),
  );

  // Table columns definition
  const columns: TableColumn<ArticleProfile>[] = [
    {
      key: 'title',
      header: 'Title',
      sortable: true,
      render: (content: ArticleProfile, value: string) => (
        <Box minW="180px" py={1}>
          <Text fontWeight="semibold" lineHeight="1.4">
            {value}
          </Text>
          {content.author && (
            <Text fontSize="xs" color={muted} mt={0.5}>
              {content.author}
            </Text>
          )}
        </Box>
      ),
    },
    {
      key: 'grade',
      header: 'Grade',
      sortable: true,
      render: (_content: ArticleProfile, value: string) => (
        <Badge colorScheme="blue" variant="subtle">
          Grade {value}
        </Badge>
      ),
    },
    {
      key: 'chapter',
      header: 'Chapter',
      sortable: true,
      render: (_content: ArticleProfile, value: string) => (
        <Badge colorScheme="green" variant="subtle">
          Ch. {value}
        </Badge>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      sortable: true,
      render: (_content: ArticleProfile, value: string) => {
        const date = new Date(value);
        const isRecent = date > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return (
          <HStack spacing={2}>
            <span style={{ fontSize: '0.875rem' }}>
              {date.toLocaleDateString()}
            </span>
            {isRecent && (
              <Badge colorScheme="orange" size="sm">
                New
              </Badge>
            )}
          </HStack>
        );
      },
    },
  ];

  const hasFilters = Boolean(searchQuery || selectedGrade || selectedChapter);
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedGrade('');
    setSelectedChapter('');
  };

  return (
    <AdminLayout padding={3}>
      <VStack spacing={4} align="stretch">
        <Box>
          <Breadcrumb
            items={[
              { label: 'Admin', href: '/admin' },
              { label: 'Library', isCurrent: true },
            ]}
          />
          <Flex
            align="center"
            justify="space-between"
            gap={3}
            wrap="wrap"
            mt={2}
          >
            <Box>
              <Heading size="md">Homework library</Heading>
              <Text fontSize="sm" color={muted} mt={1}>
                Find, preview, and manage reading assignments.
              </Text>
            </Box>
            <Button
              size="sm"
              colorScheme="blue"
              leftIcon={<FiPlus />}
              onClick={handleAddNew}
            >
              Add New Homework
            </Button>
          </Flex>
        </Box>

        <AdminSummary
          loading={isLoading}
          items={[
            { label: 'Articles', value: stats.totalArticles },
            { label: 'Added this week', value: stats.recentArticles },
            { label: 'Grades', value: Object.keys(stats.byGrade).length },
            { label: 'Chapters', value: Object.keys(stats.byChapter).length },
          ]}
        />

        <Box
          bg={surface}
          borderWidth="1px"
          borderColor={border}
          borderRadius="lg"
          p={3}
        >
          <Stack direction={{ base: 'column', md: 'row' }} spacing={2}>
            <InputGroup size="sm" flex={1} minW={{ md: '220px' }}>
              <InputLeftElement pointerEvents="none">
                <FiSearch />
              </InputLeftElement>
              <Input
                aria-label="Search library"
                placeholder="Search title, grade, or chapter"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                borderRadius="md"
              />
            </InputGroup>
            <Stack direction={{ base: 'column', sm: 'row' }} spacing={2}>
              <Select
                aria-label="Filter by grade"
                size="sm"
                borderRadius="md"
                placeholder="All Grades"
                value={selectedGrade}
                onChange={(event) => setSelectedGrade(event.target.value)}
                w={{ base: 'full', md: '140px' }}
              >
                {availableGrades.map((grade) => (
                  <option key={grade} value={grade}>
                    Grade {grade}
                  </option>
                ))}
              </Select>
              <Select
                aria-label="Filter by chapter"
                size="sm"
                borderRadius="md"
                placeholder="All Chapters"
                value={selectedChapter}
                onChange={(event) => setSelectedChapter(event.target.value)}
                w={{ base: 'full', md: '150px' }}
              >
                {availableChapters.map((chapter) => (
                  <option key={chapter} value={chapter}>
                    Chapter {chapter}
                  </option>
                ))}
              </Select>
              {hasFilters && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={clearFilters}
                  flexShrink={0}
                >
                  Clear
                </Button>
              )}
            </Stack>
          </Stack>
          <Flex
            align="center"
            justify="space-between"
            gap={2}
            wrap="wrap"
            mt={2}
          >
            <Text fontSize="xs" color={muted} role="status">
              {isLoading
                ? 'Loading articles…'
                : `${filteredContents.length} of ${stats.totalArticles} articles`}
            </Text>
            <HStack spacing={3} flexWrap="wrap">
              <FormControl display="flex" alignItems="center" width="auto">
                <FormLabel
                  htmlFor="admin-tools"
                  mb={0}
                  mr={2}
                  fontSize="xs"
                  color={muted}
                >
                  Admin tools
                </FormLabel>
                <Switch
                  id="admin-tools"
                  size="sm"
                  isChecked={adminToolsEnabled}
                  onChange={(event) =>
                    setAdminToolsEnabled(event.target.checked)
                  }
                />
              </FormControl>
              {adminToolsEnabled && (
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="purple"
                  isLoading={isUpdatingWordCount}
                  loadingText="Updating"
                  onClick={handleUpdateWordCount}
                >
                  Update Word Count
                </Button>
              )}
            </HStack>
          </Flex>
        </Box>

        <DataTable
          {...{
            data: filteredContents,
            size: 'sm',
            compact: true,
            striped: false,
            error: libraryError
              ? new Error(
                  'Could not load the library. Please refresh to try again.',
                )
              : undefined,
            columns,
            loading: isLoading,
            clickableRows: true,
            onRowClick: handleRowClick,
            actions: [
              {
                label: 'Preview',
                icon: <FiEye />,
                onClick: handleView,
                variant: 'primary' as const,
              },
              {
                label: 'Edit',
                icon: <FiEdit />,
                onClick: handleEdit,
                variant: 'secondary' as const,
              },
            ],
            pagination: {
              currentPage: currentPage,
              totalPages: totalPages,
              totalItems: totalItems,
              pageSize: pageSize,
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange,
              showPageSizeSelector: true,
            },
            emptyState: (
              <VStack spacing={2} py={8} color={muted} textAlign="center">
                <FiBookOpen size={28} />
                <Text fontWeight="600">
                  {hasFilters ? 'No matching homework' : 'No homework yet'}
                </Text>
                <Text fontSize="sm">
                  {hasFilters
                    ? 'Try another title, grade, or chapter.'
                    : 'Add homework to build your reading library.'}
                </Text>
                {hasFilters && (
                  <Button size="sm" variant="ghost" onClick={clearFilters}>
                    Clear filters
                  </Button>
                )}
              </VStack>
            ),
          }}
        />
      </VStack>
    </AdminLayout>
  );
};
